import {
  createContext,
  useContext,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import type { MedSection } from '../hook/types';
import { useGetAllCatalogMeds } from '../../../components/Users/MedicationOrderSets/hooks';
import type { MedicationCatalog } from '../../../components/Users/MedicationOrderSets/types';
import { defaultMedSectionBreak } from '../types';
import { mapToMedicationDetails } from '../../../components/Users/OrderSetForm/helper';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../../store';

interface MedicationProfileCatalogueContextType {
  medSections: MedSection[];
  setMedSections: (sections: MedSection[]) => void;
  medCatalogData: Record<string, MedicationCatalog[]>[];
  isLoadingCatalogue: boolean;
  errorMessage: string;
  addSelectedCatalogMedsToSections: (selected: MedicationCatalog[]) => void;
}

const MedicationProfileCatalogueContext = createContext<
  MedicationProfileCatalogueContextType | undefined
>(undefined);

export const useMedicationProfileCatalogue = () => {
  const context = useContext(MedicationProfileCatalogueContext);
  if (!context) {
    throw new Error(
      'useMedicationProfileCatalogue must be used within a MedicationProfileCatalogueProvider',
    );
  }
  return context;
};

export const MedicationProfileCatalogueProvider = ({
  children,
  enabled = true,
  medSections,
  setMedSections,
  onCloseCatalogueSection,
}: {
  children: ReactNode;
  enabled?: boolean;
  medSections: MedSection[];
  onCloseCatalogueSection: () => void;
  setMedSections: (sections: MedSection[]) => void;
}) => {
  const medSectionsRef = useRef<MedSection[]>(medSections);
  useEffect(() => {
    medSectionsRef.current = medSections;
  }, [medSections]);

  const onCloseCatalogueSectionRef = useRef(onCloseCatalogueSection);
  useEffect(() => {
    onCloseCatalogueSectionRef.current = onCloseCatalogueSection;
  }, [onCloseCatalogueSection]);

  const lastRequestedKeyRef = useRef<string>('');
  const requiredOrderStatusCode = 'AUA1';

  const { rawEvents, latestEpisodeId } = useSelector(
    (state: RootState) => state.timeline,
  );
  const mpIds = useMemo(() => {
    const results = new Set<string>();
    if (!rawEvents || typeof rawEvents !== 'object') return [];

    const eventArrays: any[][] = [];
    const traverse = (node: any): void => {
      if (!node) return;
      if (Array.isArray(node)) {
        node.forEach((child) => traverse(child));
        return;
      }
      if (typeof node === 'object') {
        if (Array.isArray((node as any).events)) {
          eventArrays.push((node as any).events);
        }
        for (const val of Object.values(node)) {
          traverse(val);
        }
      }
    };
    traverse(rawEvents);

    for (const events of eventArrays) {
      for (const ev of events) {
        if (
          latestEpisodeId !== undefined &&
          latestEpisodeId !== null &&
          String(ev?.episode_id ?? '') !== String(latestEpisodeId)
        ) {
          continue;
        }

        const mpId: string | undefined = ev?.detail?.mp_id;
        const fhir = ev?.detail?.fhir;
        if (!mpId || !fhir) continue;

        const isCompleted =
          fhir?.status === 'completed' || fhir?.status === 'Active';
        const isEmrOrder = ev?.detail?.emrOrder === true;
        const isHomeMedication = ev?.detail?.homeMedication === true;
        if (!(isCompleted && (isEmrOrder || isHomeMedication))) {
          continue;
        }

        const status: string = String(
          ev?.detail?.orderstatuscode ?? ev?.detail?.orderStatusCode ?? '',
        );
        if (status !== requiredOrderStatusCode) {
          continue;
        }

        results.add(String(mpId));
      }
    }

    return Array.from(results);
  }, [rawEvents, latestEpisodeId]);

  const {
    postGetAllCatalogMeds,
    isLoading: isLoadingCatalogue,
    errorMessage,
    medCatalogData,
  } = useGetAllCatalogMeds();

  const effectiveIsLoadingCatalogue = enabled ? isLoadingCatalogue : false;
  const effectiveErrorMessage = enabled ? errorMessage : '';
  const effectiveMedCatalogData = enabled ? medCatalogData : [];

  const addSelectedCatalogMedsToSections = (selected: MedicationCatalog[]) => {
    if (!Array.isArray(selected) || selected.length === 0) return;

    const selectedMpIds = new Set(
      selected
        .map(
          (m: any) =>
            m?.mp_id_from_med_profile ?? m?.mp_id ?? m?.amt?.mpId ?? null,
        )
        .filter(Boolean)
        .map((v) => String(v)),
    );

    const medicationsToAdd = selected.map(mapToMedicationDetails);
    const currentSections = Array.isArray(medSectionsRef.current)
      ? medSectionsRef.current
      : [];
    const existingIds = new Set<string>();

    for (const section of currentSections) {
      if (!Array.isArray(section.medications)) continue;
      for (const m of section.medications) {
        if (m && (m as any).medId !== undefined) {
          existingIds.add(String((m as any).medId));
        }
      }
    }

    const deduped = medicationsToAdd.filter(
      (m) => !existingIds.has(String((m as any).medId)),
    );
    if (deduped.length === 0) return;

    const prunedSections = currentSections.map((section) => ({
      ...section,
      medications: Array.isArray(section.medications)
        ? section.medications.filter((m: any) => {
            if (selectedMpIds.size === 0) return true;
            const mpId = m?.amt?.mpId;
            return !selectedMpIds.has(String(mpId));
          })
        : section.medications,
    }));

    const targetIndex = currentSections.findIndex(
      (s) => s.medSectionBreak === defaultMedSectionBreak,
    );

    if (targetIndex >= 0) {
      const target = prunedSections[targetIndex];
      const nextSections = prunedSections.map((s, idx) => {
        if (idx !== targetIndex) return s;
        return {
          ...target,
          medications: [
            ...(Array.isArray(target.medications) ? target.medications : []),
            ...deduped,
          ],
        };
      });
      setMedSections(nextSections);
      return;
    }

    if (currentSections.length > 0) {
      const first = prunedSections[0];
      const nextSections = prunedSections.map((s, idx) => {
        if (idx !== 0) return s;
        return {
          ...first,
          medications: [
            ...(Array.isArray(first.medications) ? first.medications : []),
            ...deduped,
          ],
        };
      });
      setMedSections(nextSections);
      return;
    }

    setMedSections([
      { medSectionBreak: defaultMedSectionBreak, medications: deduped } as any,
    ]);
  };

  useEffect(() => {
    if (!enabled) return;
    if (mpIds.length === 0) return;
    const requestKey = mpIds.map(String).sort().join('|');
    if (requestKey === lastRequestedKeyRef.current) return;
    lastRequestedKeyRef.current = requestKey;

    (async () => {
      const data = await postGetAllCatalogMeds({
        filterPayload: { mpIds },
        medProfileCatalogData: true,
      });

      if (!Array.isArray(data)) return;
      const hasAnyMatch = data.some((group: any) => {
        if (!group || typeof group !== 'object') return false;
        return Object.values(group).some(
          (v: any) => Array.isArray(v) && v.length > 0,
        );
      });

      if (!hasAnyMatch) {
        onCloseCatalogueSectionRef.current?.();
        return;
      }

      const mpIdsToRemove: Array<string | number> = [];
      const medNamesToRemove = new Set<string>();
      const normalize = (value: unknown) =>
        String(value ?? '')
          .trim()
          .toLowerCase();

      for (const group of data) {
        if (!group || typeof group !== 'object') continue;
        const title = Object.keys(group as any)?.[0];
        if (title) medNamesToRemove.add(normalize(title));
        for (const meds of Object.values(group as any)) {
          if (!Array.isArray(meds)) continue;
          for (const med of meds) {
            const mpId =
              med?.mp_id_from_med_profile ??
              med?.mp_id ??
              med?.amt?.mpId ??
              undefined;
            if (mpId) mpIdsToRemove.push(mpId);
          }
        }
      }

      const idSet = new Set(mpIdsToRemove.map((id) => String(id)));
      const nextSections = (
        Array.isArray(medSectionsRef.current) ? medSectionsRef.current : []
      ).map((section) => ({
        ...section,
        medications: Array.isArray(section.medications)
          ? section.medications.filter((m: any) => {
              const mpId = m?.amt?.mpId;
              const medName = m?.medName ?? m?.med_name;
              const matchByMpId =
                mpIdsToRemove.length > 0 ? idSet.has(String(mpId)) : false;
              const matchByName = medNamesToRemove.has(normalize(medName));
              return !matchByMpId && !matchByName;
            })
          : section.medications,
      }));
      setMedSections(nextSections);
    })();
  }, [enabled, mpIds, postGetAllCatalogMeds, setMedSections]);

  return (
    <MedicationProfileCatalogueContext.Provider
      value={{
        medSections,
        setMedSections,
        medCatalogData: effectiveMedCatalogData,
        isLoadingCatalogue: effectiveIsLoadingCatalogue,
        errorMessage: effectiveErrorMessage,
        addSelectedCatalogMedsToSections,
      }}
    >
      {children}
    </MedicationProfileCatalogueContext.Provider>
  );
};
